import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Batches from "@/pages/Batches";
import BatchDetail from "@/pages/BatchDetail";
import BatchSummaryPage from "@/pages/BatchSummaryPage";
import Allocations from "@/pages/Allocations";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Customers from "@/pages/Customers";
import Team from "@/pages/Team";
import Masters from "@/pages/Masters";
import Receiving from "@/pages/Receiving";
import ReceivingSession from "@/pages/ReceivingSession";
import Approvals from "@/pages/Approvals";
import Packing from "@/pages/Packing";
import PackingJob from "@/pages/PackingJob";
import Shipping from "@/pages/Shipping";
import ShippingDetail from "@/pages/ShippingDetail";
import Invoices from "@/pages/Invoices";
import OwnerReview from "@/pages/OwnerReview";
import Department from "@/pages/Department";
import ArchiveFolder from "@/pages/ArchiveFolder";
import ArchiveList from "@/pages/ArchiveList";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="grid min-h-screen place-items-center" data-testid="app-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

function RoleGuard({ allow, children }) {
  const { user } = useAuth();
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/" element={<Batches />} />
              <Route path="/dept/:deptKey" element={<Department />} />
              <Route path="/archive" element={<ArchiveList />} />
              <Route path="/archive/:batchId" element={<ArchiveFolder />} />
              <Route path="/batches/:batchId" element={<BatchDetail />} />
              <Route path="/batches/:batchId/summary" element={<BatchSummaryPage />} />
              <Route path="/orders" element={<RoleGuard allow={["owner", "admin_input", "viewer"]}><Orders /></RoleGuard>} />
              <Route path="/orders/:orderId" element={<RoleGuard allow={["owner", "admin_input", "viewer"]}><OrderDetail /></RoleGuard>} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/allocations" element={<Allocations />} />
              <Route path="/team" element={<Team />} />
              <Route path="/masters" element={<RoleGuard allow={["owner"]}><Masters /></RoleGuard>} />
              <Route path="/receiving" element={<Receiving />} />
              <Route path="/receiving/sessions/:sessionId" element={<ReceivingSession />} />
              <Route path="/receiving/approvals" element={<Approvals />} />
              <Route path="/packing" element={<Packing />} />
              <Route path="/packing/jobs/:jobId" element={<PackingJob />} />
              <Route path="/shipping" element={<RoleGuard allow={["owner", "admin_input", "viewer"]}><Shipping /></RoleGuard>} />
              <Route path="/shipping/orders/:orderId" element={<RoleGuard allow={["owner", "admin_input", "viewer"]}><ShippingDetail /></RoleGuard>} />
              <Route path="/owner-review" element={<RoleGuard allow={["owner"]}><OwnerReview /></RoleGuard>} />
              <Route path="/invoices" element={<RoleGuard allow={["owner", "admin_input", "admin"]}><Invoices /></RoleGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
